/**
 * The local database — what every screen reads. No screen awaits the network
 * to render (Lift's rule, same reason: a kitchen with bad wifi must behave like
 * one with good wifi). Sync moves rows between here and the server in the
 * background, through the outbox.
 */

import Dexie, { type Table } from 'dexie'

import type { Nutrients } from '../lib/nutrients'
import type { ServingDef } from '../lib/servings'
import type { Activity, DietStyle, GoalType, ProteinPref, Sex } from '../lib/targets'

type Synced = { id: string; updated_at: string; deleted_at?: string | null; seq?: number }

export type Source = 'usda' | 'cnf' | 'restaurant' | 'off' | 'custom' | 'recipe' | 'meal' | 'ai_photo' | 'ai_text' | 'label' | 'quick'

export type FoodRow = Synced & {
  day: string
  meal_slot: number
  logged_at?: string | null
  order_index: number
  source: Source
  source_ref?: string | null
  name: string
  brand?: string | null
  grams?: number | null
  serving_label?: string | null
  serving_qty?: number | null
  per100?: Nutrients | null
  nutrients?: Nutrients | null
  kcal: number
  protein_g: number
  carbs_g: number
  fat_g: number
  fibre_g?: number | null
  group_id?: string | null
  group_label?: string | null
  ai_confidence?: 'high' | 'medium' | 'low' | null
  notes?: string | null
}

export type DayStatusRow = Synced & { day: string; status: 'auto' | 'complete' | 'incomplete' }

export type CustomFoodRow = Synced & {
  name: string
  brand?: string | null
  barcode?: string | null
  basis: 'g' | 'ml'
  per100?: Nutrients | null
  servings?: ServingDef[] | null
  source?: string | null
  last_used_at?: string | null
  notes?: string | null
}

export type RecipeRow = Synced & {
  name: string
  servings: number
  cooked_weight_g?: number | null
  ingredients?: { name: string; brand?: string | null; grams: number; per100: Nutrients;
    source?: string | null; source_ref?: string | null }[] | null
  per100?: Nutrients | null
  per_serving?: Nutrients | null
  total_grams?: number | null
  last_used_at?: string | null
  notes?: string | null
}

export type MealItem = {
  name: string; brand?: string | null; grams?: number | null; per100?: Nutrients | null
  nutrients: Nutrients; serving_label?: string | null; serving_qty?: number | null
  source?: string | null; source_ref?: string | null
}

export type SavedMealRow = Synced & { name: string; items?: MealItem[] | null; last_used_at?: string | null }

export type WeighinRow = Synced & {
  measured_at: string
  weight_kg?: number | null
  bodyfat_pct?: number | null
  notes?: string | null
  relay_state?: 'synced' | 'pending' | 'failed'
}

export type TargetRow = Synced & {
  effective_from: string
  kcal: number
  protein_g: number
  carbs_g: number
  fat_g: number
  fibre_g?: number | null
  goal_type?: GoalType | null
  goal_rate_kg_wk?: number | null
  expenditure?: number | null
  source?: 'initial' | 'checkin' | 'goal_change' | 'manual' | null
}

export type CheckinRow = Synced & {
  day: string
  inputs?: Record<string, unknown> | null
  proposed?: Record<string, unknown> | null
  action?: 'accepted' | 'edited' | 'kept' | null
  target_id?: string | null
}

export type ProfileRow = Synced & {
  sex?: Sex | null
  birth_date?: string | null
  height_cm?: number | null
  activity?: Activity | null
  goal_type?: GoalType | null
  goal_rate_kg_wk?: number | null
  goal_weight_kg?: number | null
  protein_pref?: ProteinPref | null
  diet_style?: DietStyle | null
  meal_names?: string[] | null
  checkin_weekday?: number | null
  program_start?: string | null
  /** Shift calories toward training days. null/undefined = on. */
  training_shift?: boolean | null
}

/** A Lift workout with its estimated NET burn. Read-only: pulled from Lift. */
export type WorkoutRow = Synced & {
  day: string
  title?: string | null
  started_at?: string | null
  minutes: number
  strength_min: number
  cardio_min: number
  kcal: number
  duration_basis?: string | null
  parts?: { label: string; kcal: number }[] | null
}

export type Entity =
  | 'food' | 'day_status' | 'custom_food' | 'recipe' | 'saved_meal'
  | 'weighin' | 'target' | 'checkin' | 'profile' | 'workout'

export type OutboxOp = {
  op_id: string
  entity: Entity
  op: 'upsert' | 'delete'
  row_id: string
  payload: Record<string, unknown>
  created_at: number
  attempts: number
  last_error?: string | null
}

export type MetaRow = { key: string; value: unknown }

export class MacroDB extends Dexie {
  foods!: Table<FoodRow, string>
  day_status!: Table<DayStatusRow, string>
  custom_foods!: Table<CustomFoodRow, string>
  recipes!: Table<RecipeRow, string>
  saved_meals!: Table<SavedMealRow, string>
  weighins!: Table<WeighinRow, string>
  targets!: Table<TargetRow, string>
  checkins!: Table<CheckinRow, string>
  profiles!: Table<ProfileRow, string>
  workouts!: Table<WorkoutRow, string>
  outbox!: Table<OutboxOp, string>
  meta!: Table<MetaRow, string>

  constructor() {
    super('macro')
    this.version(1).stores({
      foods: 'id, day, [day+meal_slot], source_ref, name, updated_at',
      day_status: 'id, day',
      custom_foods: 'id, name, barcode, last_used_at',
      recipes: 'id, name, last_used_at',
      saved_meals: 'id, name, last_used_at',
      weighins: 'id, measured_at',
      targets: 'id, effective_from',
      checkins: 'id, day',
      profiles: 'id',
      outbox: 'op_id, row_id, created_at',
      meta: 'key',
    })
    // v2: Lift workouts (read-only, pulled). Only the new table is declared.
    this.version(2).stores({ workouts: 'id, day' })
  }
}

export const db = new MacroDB()

/** entity name (wire) -> Dexie table. The client half of the server registry. */
export const TABLES: Record<Entity, Table<any, string>> = {
  food: db.foods,
  day_status: db.day_status,
  custom_food: db.custom_foods,
  recipe: db.recipes,
  saved_meal: db.saved_meals,
  weighin: db.weighins,
  target: db.targets,
  checkin: db.checkins,
  profile: db.profiles,
  workout: db.workouts,
}

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key)
  return row === undefined ? fallback : (row.value as T)
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value })
}

export const CURSOR_KEY = 'sync.cursor'
export const OWNER_KEY = 'auth.user_id'

export async function wipeLocal(): Promise<void> {
  await db.transaction('rw', [...Object.values(TABLES), db.outbox, db.meta], async () => {
    await Promise.all([...Object.values(TABLES).map((t) => t.clear()), db.outbox.clear()])
    await db.meta.delete(CURSOR_KEY)
  })
}
